import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import "./UploadGuestPhotos.css";
import { FileUploader } from "react-drag-drop-files";
import { Alert, Button } from "@mui/material";
import SponsoredSlot from "../Sponsored/SponsoredSlot";

const allowedExtensions = ["jpg", "jpeg", "png", "webp"];
const fileTypes = allowedExtensions.map((ext) => ext.toUpperCase());
const API = process.env.REACT_APP_API_BASE;

const decodeWeddingToken = (token) => {
  if (!token) {
    return null;
  }

  try {
    const decoded = atob(token);
    const payload = JSON.parse(decoded);

    if (
      payload &&
      (typeof payload.weddingId === "number" || typeof payload.weddingId === "string")
    ) {
      return String(payload.weddingId);
    }
  } catch (error) {
    console.error("Failed to decode wedding token:", error);
  }

  return null;
};

const FileUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const directWeddingId = queryParams.get("wedding_id");
  const token = queryParams.get("token");
  const weddingId = useMemo(
    () => directWeddingId || decodeWeddingToken(token),
    [directWeddingId, token]
  );

  const normalizeFiles = (incomingFiles) => {
    if (!incomingFiles) {
      return [];
    }

    if (Array.isArray(incomingFiles)) {
      return incomingFiles;
    }

    if (
      typeof FileList !== "undefined" &&
      incomingFiles instanceof FileList
    ) {
      return Array.from(incomingFiles);
    }

    return [incomingFiles];
  };

  const getFileExtension = (file) => {
    if (!file?.name) {
      return "";
    }

    const parts = file.name.split(".");
    if (parts.length <= 1) {
      return "";
    }

    return parts.pop().toLowerCase();
  };

  const handleDrop = (incomingFiles) => {
    const normalizedFiles = normalizeFiles(incomingFiles);

    if (!normalizedFiles.length) {
      return;
    }

    const { validFiles, invalidFiles } = normalizedFiles.reduce(
      (acc, file) => {
        const extension = getFileExtension(file);
        const mimeExtension = file.type?.split("/").pop()?.toLowerCase();
        const isAllowed =
          allowedExtensions.includes(extension) ||
          (mimeExtension && allowedExtensions.includes(mimeExtension));

        if (isAllowed) {
          acc.validFiles.push(file);
        } else {
          acc.invalidFiles.push(file.name || "Непозната датотека");
        }
        return acc;
      },
      { validFiles: [], invalidFiles: [] }
    );

    const hasInvalid = invalidFiles.length > 0;

    if (hasInvalid) {
      setErrorMessage(
        `Следниве датотеки не се поддржани: ${invalidFiles.join(", ")}. Поддржани формати се: ${fileTypes.join(", ")}.`
      );
      setSuccessMessage("");
    }

    if (!validFiles.length) {
      return;
    }

    setFiles((prevFiles) => {
      const nextFiles = [...prevFiles];

      validFiles.forEach((file) => {
        const isDuplicate = nextFiles.some(
          (existingFile) =>
            existingFile.name === file.name &&
            existingFile.size === file.size &&
            existingFile.lastModified === file.lastModified
        );

        if (!isDuplicate) {
          nextFiles.push(file);
        }
      });

      return nextFiles;
    });
    if (!hasInvalid) {
      setErrorMessage("");
    }
    setSuccessMessage("");
  };

  const handleRemoveFile = (fileToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          )
      )
    );
  };

  const handleSubmit = async () => {
    if (!weddingId) {
      setErrorMessage(
        "Линкот е невалиден. Скенирајте го QR кодот повторно."
      );
      setSuccessMessage("");
      return;
    }

    if (!files.length) {
      setErrorMessage("Додадете барем една фотографија пред прикачување.");
      setSuccessMessage("");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await fetch(
        `${API}/guest-qr-upload-images?wedding_id=${weddingId}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const responseData = await response.json();
      console.log(responseData);
      setSuccessMessage("Фотографиите се прикачени успешно. Ви благодариме!");
      setFiles([]);
      setUploaderKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.error("Failed to upload guest photos:", error);
      setErrorMessage("Не успеавме да ги прикачиме фотографиите. Обидете се повторно.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container">
      {!weddingId && (
        <Alert severity="warning" className="statusMessage">
          Прикачувањето е оневозможено додека не се скенира точниот QR код.
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" className="statusMessage">
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" className="statusMessage">
          {successMessage}
        </Alert>
      )}
      <FileUploader
        key={uploaderKey}
        handleChange={handleDrop}
        name="file"
        types={fileTypes}
        multiple
        uploadedLabel="Successful upload"
      >
        <div className="dropPhotosContainer"></div>
      </FileUploader>
      {files.length > 0 && (
        <ul className="selectedFilesList">
          {files.map((file) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="selectedFileItem"
            >
              <span className="selectedFileName">{file.name}</span>
              <button
                type="button"
                className="removeFileButton"
                onClick={() => handleRemoveFile(file)}
                disabled={isSubmitting}
              >
                Избриши
              </button>
            </li>
          ))}
        </ul>
      )}
      <Button
        className="uploadButton"
        variant="contained"
        onClick={handleSubmit}
        disabled={isSubmitting || !files.length || !weddingId}
      >
        {isSubmitting ? "Прикачување..." : "Прикачи"}
      </Button>
      <SponsoredSlot placement="upload-photos.bottom" />
    </div>
  );
};
export default FileUpload;
